import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "../utils/axios";
import { isValidEmail } from "../utils/validators";
import "./Contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    type: null, // "success" or "error"
    message: ""
  });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length > 100) {
      newErrors.name = "Name must be less than 100 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.phone && formData.phone.length > 20) {
      newErrors.phone = "Phone number must be less than 20 characters";
    }

    if (formData.company && formData.company.length > 100) {
      newErrors.company = "Company name must be less than 100 characters";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (formData.subject.length > 200) {
      newErrors.subject = "Subject must be less than 200 characters";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.length > 2000) {
      newErrors.message = "Message must be less than 2000 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const response = await axios.post('/api/contact', formData);
      
      if (response.data.success) {
        setSubmitStatus({
          type: "success",
          message: "Thank you for contacting us! We will get back to you shortly."
        });
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: ""
        });
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setSubmitStatus({
        type: "error",
        message: error.response?.data?.error || "Failed to submit contact form. Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <header className="contact-header">
        <div className="header-content">
          <Link className="logo-link" to="/">
            <span className="logo-icon">B</span>
            <span className="logo-text">BookBridge</span>
          </Link>
          <nav className="nav-links">
            <Link className="nav-link" to="/">Home</Link>
            <Link className="nav-link" to="/terms">Terms</Link>
            <Link className="nav-link" to="/privacy">Privacy</Link>
            <Link className="nav-link active" to="/contact-us">Contact</Link>
          </nav>
        </div>
      </header>

      <div className="contact-container">
        <div className="contact-wrapper">
        <div className="contact-info">
          <h1>Contact Us</h1>
          <p className="contact-subtitle">
            Have questions or need assistance? We're here to help. Fill out the form below and we'll get back to you as soon as possible.
          </p>

          <div className="contact-details">
            <div className="contact-item">
              <div className="contact-icon">📧</div>
              <div className="contact-text">
                <h3>Email</h3>
                <p>support@xcelerateai-api.com</p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">🌐</div>
              <div className="contact-text">
                <h3>Website</h3>
                <p>https://qbo.xcelerateai-api.com</p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">⏰</div>
              <div className="contact-text">
                <h3>Response Time</h3>
                <p>Typically within 24-48 hours</p>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-form-container">
          <form onSubmit={handleSubmit} className="contact-form" noValidate>
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "error" : ""}
                placeholder="Enter your full name"
                maxLength={100}
              />
              {errors.name && (
                <div className="error-message">
                  {errors.name}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? "error" : ""}
                placeholder="Enter your email address"
                maxLength={255}
              />
              {errors.email && (
                <div className="error-message">
                  {errors.email}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? "error" : ""}
                placeholder="Enter your phone number"
                maxLength={20}
              />
              {errors.phone && (
                <div className="error-message">
                  {errors.phone}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="company">Company Name (Optional)</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={errors.company ? "error" : ""}
                placeholder="Enter your company name"
                maxLength={100}
              />
              {errors.company && (
                <div className="error-message">
                  {errors.company}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={errors.subject ? "error" : ""}
                placeholder="Enter the subject of your inquiry"
                maxLength={200}
              />
              {errors.subject && (
                <div className="error-message">
                  {errors.subject}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={errors.message ? "error" : ""}
                placeholder="Describe your inquiry or issue in detail"
                rows={6}
                maxLength={2000}
              />
              <div className="char-count">
                {formData.message.length}/2000
              </div>
              {errors.message && (
                <div className="error-message">
                  {errors.message}
                </div>
              )}
            </div>

            {submitStatus.type === "error" && (
              <div className="error-message">
                {submitStatus.message}
              </div>
            )}

            {submitStatus.type === "success" && (
              <div className="alert success">
                {submitStatus.message}
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Contact;
